/**
 * Utilidades para manejar IndexedDB en la aplicación
 * Evita problemas de cuota de localStorage con grandes volúmenes de datos
 */

const DB_NAME = 'QuroReportsDB';
const DB_VERSION = 1;
const STORE_NAME = 'fechas_km';

/**
 * Abre o crea la base de datos IndexedDB
 * @returns {Promise<IDBDatabase>}
 */
function openDB() {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onupgradeneeded = function(event) {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    
    request.onsuccess = function(event) {
      resolve(event.target.result);
    };
    
    request.onerror = function(event) {
      console.error('Error opening IndexedDB:', event.target.error);
      reject(event.target.error);
    };
  });
}

/**
 * Guarda el array de fechas en IndexedDB
 * @param {Array} fechasArray - Array de datos de fechas
 * @returns {Promise<void>}
 */
export async function saveFechasArray(fechasArray) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    // Usar un ID único basado en timestamp para evitar conflictos
    const id = 'dates_km_all';
    await store.put({ 
      id, 
      data: fechasArray, 
      timestamp: Date.now() 
    });
    
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('Error saving to IndexedDB:', error);
    // Fallback a localStorage si IndexedDB falla
    try {
      // Intentar con localStorage como respaldo (con manejo de errores)
      const dataToSave = JSON.stringify(fechasArray);
      if (dataToSave.length < 4900000) { // ~5MB límite seguro
        window.localStorage.setItem('dates_km_all', dataToSave);
      } else {
        console.warn('Datos demasiado grandes para localStorage, usando solo memoria');
      }
    } catch (localStorageError) {
      console.error('Error con localStorage fallback:', localStorageError);
    }
    throw error;
  }
}

/**
 * Obtiene el array de fechas desde IndexedDB
 * @returns {Promise<Array>}
 */
export async function getFechasArray() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.get('dates_km_all');
      
      request.onsuccess = function() {
        const result = request.result;
        resolve(result ? result.data : []);
      };
      
      request.onerror = function() {
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error reading from IndexedDB:', error);
    // Fallback a localStorage si IndexedDB falla
    try {
      const localStorageData = window.localStorage.getItem('dates_km_all');
      return localStorageData ? JSON.parse(localStorageData) : [];
    } catch (localStorageError) {
      console.error('Error con localStorage fallback:', localStorageError);
      return [];
    }
  }
}

/**
 * Limpia datos antiguos de IndexedDB (opcional)
 * @param {number} maxAge - Edad máxima en milisegundos (por defecto 24 horas)
 * @returns {Promise<void>}
 */
export async function cleanOldData(maxAge = 24 * 60 * 60 * 1000) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    
    const request = store.getAll();
    request.onsuccess = function() {
      const allRecords = request.result;
      const now = Date.now();
      
      allRecords.forEach(record => {
        if (record.timestamp && (now - record.timestamp) > maxAge) {
          store.delete(record.id);
        }
      });
    };
  } catch (error) {
    console.error('Error cleaning old data:', error);
  }
}

/**
 * Verifica si IndexedDB está disponible
 * @returns {boolean}
 */
export function isIndexedDBAvailable() {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}
